import type { TableColumns } from '@ohpen/enso-ui';
import { Address, FirmType, CasePermission, ComplianceStatus, FieldType, DraftFirmOperation, DraftProcFeeBoost, FeeUnit, OperationType } from '@pmp/api';
import { OPERATION_ORDER } from './utils';
import { QUALIFIED_DOMAINS_LIST } from './useQualifiedDomainsInCR';

export type AddressWithRowNumber = Address & {
  id?: string | null;
  rowNumber: number;
  isActive?: boolean | null;
  region?: string | null;
  businessDevelopmentManager?: string | null;
};

// Extended type: replaces tradingAddressIds with tradingAddresses (Array<Address>)
export type FirmCRDataRow = {
  rowNumber: number;
  id?: string | null;
  ohpenId?: string | null;
  name: string;
  type: FirmType;
  frn?: number | null;
  principalFirmIds: Array<string>;
  packagerFirmIds?: Array<string>;
  defaultPaymentRouteFirmId?: string | null;
  paymentRouteFirmIds?: Array<string> | null;
  operationsWhitelist?: Array<DraftFirmOperation> | null;
  btl?: CasePermission | null;
  rbtl?: CasePermission | null;
  rmc?: CasePermission | null;
  procFeeBoost?: DraftProcFeeBoost | null;
  procFeeBoostValue?: number | null;
  procFeeBoostUnit?: FeeUnit | null;
  idsOfExclusivitySets?: Array<string> | null;
  isActive?: boolean | null;
  nacfbNumber?: string | null;
  companiesHouseNumber?: string | null;
  reference?: string | null;
  qualifiedDomains?: Array<string> | null;
  qualifiedDomainRequiredForSelfRegistration?: boolean | null;
  email?: string | null;
  mobilePhone?: string | null;
  workPhone?: string | null;
  website?: string | null;
  complaintsWebsite?: string | null;
  complianceStatus?: ComplianceStatus | null;
  onTheWatchlist?: boolean | null;
  sendCaseUpdatesToEmailAllowed?: boolean | null;
  firmAddress?: Address | null;
  firmAddressNumberOrBuilding?: string | null;
  firmAddressCity?: string | null;
  firmAddressPostCode?: string | null;
  firmAddressCountry?: string | null;
  firmAddressLine1?: string | null;
  firmAddressLine2?: string | null;
  firmAddressLine3?: string | null;
  firmAddressLine4?: string | null;
  firmAddressLine5?: string | null;
  tradingAddresses?: Array<AddressWithRowNumber> | null;
  lockedByActionId?: string | null;
};

export type TradingAddressRow = Omit<Address, 'lineItems'> & {
  id?: string | null;
  rowNumber: number;
  firmRowNumber: number;
  firmName: string;
  isActive?: boolean | null;
  region?: string | null;
  businessDevelopmentManager?: string | null;
  line1?: string | null;
  line2?: string | null;
  line3?: string | null;
  line4?: string | null;
  line5?: string | null;
};

export type OperationWhitelistRow = {
  rowNumber: number;
  firmRowNumber: number;
  firmName: string;
  type?: OperationType | null;
  accountName?: string | null;
  accountNumber?: string | null;
  sortCode?: string | null;
  bankName?: string | null;
};

export const sortOperationsByType = (ops: DraftFirmOperation[]): DraftFirmOperation[] =>
  [...ops].sort((a, b) => OPERATION_ORDER.indexOf(a.type ?? '') - OPERATION_ORDER.indexOf(b.type ?? ''));

export const buildOperationsWhitelistRows = (firms: FirmCRDataRow[]): OperationWhitelistRow[] => {
  let rowNum = 0;
  return firms.flatMap(firm =>
    sortOperationsByType(firm.operationsWhitelist ?? []).map(op => ({
      rowNumber: rowNum++,
      firmRowNumber: firm.rowNumber,
      firmName: firm.name,
      type: op.type,
      accountName: op.bankAccountDetails?.accountName ?? null,
      accountNumber: op.bankAccountDetails?.accountNumber ?? null,
      sortCode: op.bankAccountDetails?.sortCode ?? null,
      bankName: op.bankAccountDetails?.bankName ?? null,
    })),
  );
};

export const operationsWhitelistCRColumns: TableColumns<OperationWhitelistRow> = [
  {
    title: 'firms.labels.firmName',
    dataIndex: 'firmName',
    key: 'firmName',
    width: 200,
    fixed: 'left',
    options: { readOnly: true },
  },
  {
    title: 'firms.labels.operationType',
    dataIndex: 'type',
    key: 'type',
    width: 160,
    options: {
      format: { type: 'valueList' },
      filter: 'select',
      listName: FieldType.OPERATION_TYPE,
    },
  },
  {
    title: 'firms.labels.accountName',
    dataIndex: 'accountName',
    key: 'accountName',
    width: 200,
  },
  {
    title: 'firms.labels.sortCode',
    dataIndex: 'sortCode',
    key: 'sortCode',
    width: 120,
  },
  {
    title: 'firms.labels.accountNumber',
    dataIndex: 'accountNumber',
    key: 'accountNumber',
    width: 180,
  },
  {
    title: 'firms.labels.bankName',
    dataIndex: 'bankName',
    key: 'bankName',
    width: 180,
  },
];

export const buildTradingAddressRows = (firms: FirmCRDataRow[]): TradingAddressRow[] => {
  let rowNum = 0;
  return firms.flatMap(firm =>
    (firm.tradingAddresses ?? []).map(addr => {
      const { lineItems, ...addrRest } = addr;
      return {
        ...addrRest,
        id: addrRest.id ?? null,
        name: addrRest.name ?? null,
        numberOrBuilding: addrRest.numberOrBuilding ?? null,
        city: addrRest.city ?? null,
        postCode: addrRest.postCode ?? null,
        country: addrRest.country ?? null,
        isActive: addrRest.isActive ?? null,
        region: addrRest.region ?? null,
        businessDevelopmentManager: addrRest.businessDevelopmentManager ?? null,
        rowNumber: rowNum++,
        firmRowNumber: firm.rowNumber,
        firmName: firm.name,
        line1: lineItems?.[0] ?? null,
        line2: lineItems?.[1] ?? null,
        line3: lineItems?.[2] ?? null,
        line4: lineItems?.[3] ?? null,
        line5: lineItems?.[4] ?? null,
      };
    }),
  );
};

export const tradingAddressCRColumns: TableColumns<TradingAddressRow> = [
  {
    title: 'firms.labels.firmName',
    dataIndex: 'firmName',
    key: 'firmName',
    width: 200,
    fixed: 'left',
    options: { readOnly: true },
  },
  {
    title: 'firms.labels.tradingName',
    dataIndex: 'name',
    key: 'name',
    width: 160,
  },
  {
    title: 'firms.labels.isActive',
    dataIndex: 'isActive',
    key: 'isActive',
    width: 100,
    options: {
      format: { type: 'boolean' },
      filter: 'select',
      listName: FieldType.YES_NO,
    },
  },
  {
    title: 'firms.labels.town',
    dataIndex: 'city',
    key: 'city',
    width: 140,
  },
  {
    title: 'firms.labels.county',
    dataIndex: 'line5',
    key: 'line5',
    width: 160,
  },
  {
    title: 'firms.labels.postCode',
    dataIndex: 'postCode',
    key: 'postCode',
    width: 120,
  },
  {
    title: 'firms.labels.country',
    dataIndex: 'country',
    key: 'country',
    width: 140,
  },
  {
    title: 'firms.labels.addressLine1',
    dataIndex: 'line1',
    key: 'line1',
    width: 160,
  },
  {
    title: 'firms.labels.addressLine2',
    dataIndex: 'line2',
    key: 'line2',
    width: 160,
  },
  {
    title: 'firms.labels.addressLine3',
    dataIndex: 'line3',
    key: 'line3',
    width: 160,
  },
  {
    title: 'firms.labels.addressLine4',
    dataIndex: 'line4',
    key: 'line4',
    width: 160,
  },
  {
    // Read-only like BDM below: only the firm form sets regionId; a free-text edit here would
    // change the display name only.
    title: 'firms.labels.region',
    dataIndex: 'region',
    key: 'region',
    width: 160,
    options: { readOnly: true },
  },
  {
    title: 'firms.labels.businessDevelopmentManager',
    dataIndex: 'businessDevelopmentManager',
    key: 'businessDevelopmentManager',
    width: 200,
    // Read-only here: the BDM must be picked from the region-filtered dropdown in the firm form so
    // bdmId is set. A free-text edit would only set the display name, which the BRS write ignores.
    options: { readOnly: true },
  },
];

export const firmCRColumns: TableColumns<FirmCRDataRow> = [
  {
    title: 'firms.labels.firmName',
    dataIndex: 'name',
    key: 'name',
    width: 200,
    fixed: 'left',
  },
  {
    title: 'firms.labels.ohpenId',
    dataIndex: 'ohpenId',
    key: 'ohpenId',
    width: 200,
    options: {
      readOnly: true,
    },
  },
  {
    title: 'firms.labels.frn',
    dataIndex: 'frn',
    key: 'frn',
    width: 120,
  },
  {
    title: 'firms.labels.isActive',
    dataIndex: 'isActive',
    key: 'isActive',
    width: 100,
    options: {
      format: {
        type: 'boolean',
      },
      filter: 'select',
      listName: FieldType.YES_NO,
    },
  },
  {
    title: 'firms.labels.type',
    dataIndex: 'type',
    key: 'type',
    width: 160,
    options: {
      format: {
        type: 'valueList',
      },
      filter: 'select',
      listName: FieldType.FIRM_TYPE,
    },
  },
  {
    title: 'firms.labels.principalFirm',
    dataIndex: 'principalFirmIds',
    key: 'principalFirmIds',
    width: 200,
    options: {
      format: {
        type: 'multiValueList',
      },
      filter: 'select',
      listName: FieldType.PRINCIPALS,
      allowSearch: true,
    },
  },
  {
    title: 'firms.labels.reference',
    dataIndex: 'reference',
    key: 'reference',
    width: 120,
  },
  {
    title: 'firms.labels.nacfbNumber',
    dataIndex: 'nacfbNumber',
    key: 'nacfbNumber',
    width: 120,
  },
  {
    title: 'firms.labels.companiesHouseNumber',
    dataIndex: 'companiesHouseNumber',
    key: 'companiesHouseNumber',
    width: 120,
  },
  {
    title: 'firms.labels.btl',
    dataIndex: 'btl',
    key: 'btl',
    width: 100,
    options: {
      format: { type: 'valueList' },
      filter: 'select',
      listName: FieldType.CASE_PERMISSION_TYPE,
    },
  },
  {
    title: 'firms.labels.rbtl',
    dataIndex: 'rbtl',
    key: 'rbtl',
    width: 100,
    options: {
      format: { type: 'valueList' },
      filter: 'select',
      listName: FieldType.CASE_PERMISSION_TYPE,
    },
  },
  {
    title: 'firms.labels.rmc',
    dataIndex: 'rmc',
    key: 'rmc',
    width: 100,
    options: {
      format: { type: 'valueList' },
      filter: 'select',
      listName: FieldType.CASE_PERMISSION_TYPE,
    },
  },
  {
    title: 'firms.labels.defaultPaymentRoute',
    dataIndex: 'defaultPaymentRouteFirmId',
    key: 'defaultPaymentRouteFirmId',
    width: 200,
    options: {
      format: {
        type: 'multiValueList',
      },
      filter: 'select',
      listName: FieldType.FIRMS,
      allowSearch: true,
    },
  },
  {
    title: 'firms.labels.paymentRoutes',
    dataIndex: 'paymentRouteFirmIds',
    key: 'paymentRouteFirmIds',
    width: 200,
    options: {
      format: {
        type: 'multiValueList',
      },
      filter: 'select',
      listName: FieldType.FIRMS,
      allowSearch: true,
    },
  },
  {
    title: 'firms.labels.ProcurationFeeBoostValue',
    dataIndex: 'procFeeBoostValue',
    key: 'procFeeBoostValue',
    width: 120,
    options: {
      format: {
        type: 'number',
      },
      filter: 'number',
    },
  },
  {
    title: 'firms.labels.ProcurationFeeBoostUnit',
    dataIndex: 'procFeeBoostUnit',
    key: 'procFeeBoostUnit',
    width: 140,
    options: {
      format: {
        type: 'valueList',
      },
      filter: 'select',
      listName: FieldType.PROCURATION_FEE_UNIT,
    },
  },
  {
    title: 'firms.labels.packagerFirm',
    dataIndex: 'packagerFirmIds',
    key: 'packagerFirmIds',
    width: 200,
    options: {
      format: {
        type: 'multiValueList',
      },
      filter: 'select',
      listName: FieldType.PACKAGERS,
      allowSearch: true,
    },
  },
  {
    title: 'firms.labels.exclusivitySets',
    dataIndex: 'idsOfExclusivitySets',
    key: 'idsOfExclusivitySets',
    width: 200,
    options: {
      format: {
        type: 'multiValueList',
      },
      filter: 'select',
      listName: FieldType.EXCLUSIVITY_SETS,
    },
  },
  {
    title: 'firms.labels.compliance',
    dataIndex: 'complianceStatus',
    key: 'complianceStatus',
    width: 140,
    options: {
      format: { type: 'valueList' },
      filter: 'select',
      listName: FieldType.COMPLIANCE_STATUS,
    },
  },
  {
    title: 'firms.labels.watchlist',
    dataIndex: 'onTheWatchlist',
    key: 'onTheWatchlist',
    width: 120,
    options: {
      format: {
        type: 'boolean',
      },
      filter: 'select',
      listName: FieldType.YES_NO,
    },
  },
];

export const contactDetailsCRColumns: TableColumns<FirmCRDataRow> = [
  {
    title: 'firms.labels.firmName',
    dataIndex: 'name',
    key: 'name',
    width: 200,
    fixed: 'left',
    options: { readOnly: true },
  },
  {
    title: 'firms.labels.mobile',
    dataIndex: 'mobilePhone',
    key: 'mobilePhone',
    width: 160,
  },
  {
    title: 'firms.labels.telephone',
    dataIndex: 'workPhone',
    key: 'workPhone',
    width: 160,
  },
  {
    title: 'firms.labels.website',
    dataIndex: 'website',
    key: 'website',
    width: 200,
  },
  {
    title: 'firms.labels.complaintsWebPage',
    dataIndex: 'complaintsWebsite',
    key: 'complaintsWebsite',
    width: 200,
  },
  {
    title: 'firms.labels.email',
    dataIndex: 'email',
    key: 'email',
    width: 200,
  },
  {
    title: 'firms.labels.qualifiedDomains',
    dataIndex: 'qualifiedDomains',
    key: 'qualifiedDomains',
    width: 200,
    options: {
      format: {
        type: 'multiTag',
      },
      listName: QUALIFIED_DOMAINS_LIST,
    },
  },
  {
    title: 'firms.labels.town',
    dataIndex: 'firmAddressCity',
    key: 'firmAddressCity',
    width: 140,
  },
  {
    title: 'firms.labels.county',
    dataIndex: 'firmAddressLine5',
    key: 'firmAddressLine5',
    width: 160,
  },
  {
    title: 'firms.labels.postCode',
    dataIndex: 'firmAddressPostCode',
    key: 'firmAddressPostCode',
    width: 120,
  },
  {
    title: 'firms.labels.country',
    dataIndex: 'firmAddressCountry',
    key: 'firmAddressCountry',
    width: 140,
  },
  {
    title: 'firms.labels.lineItem1',
    dataIndex: 'firmAddressLine1',
    key: 'firmAddressLine1',
    width: 160,
  },
  {
    title: 'firms.labels.lineItem2',
    dataIndex: 'firmAddressLine2',
    key: 'firmAddressLine2',
    width: 160,
  },
  {
    title: 'firms.labels.lineItem3',
    dataIndex: 'firmAddressLine3',
    key: 'firmAddressLine3',
    width: 160,
  },
  {
    title: 'firms.labels.lineItem4',
    dataIndex: 'firmAddressLine4',
    key: 'firmAddressLine4',
    width: 160,
  },
];
